import React from 'react';
import { Link } from 'react-router-dom';
import { FolderPlus, ArrowLeft, Terminal, FileText } from 'lucide-react';

export const FolderCreator: React.FC = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/elevation/lab"
          className="flex items-center text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Lab
        </Link>
      </div>

      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <div className="flex items-center space-x-4 mb-6">
          <div className="p-4 bg-consulting-slate-blue-50 border border-consulting-slate-blue-200">
            <FolderPlus className="h-8 w-8 text-consulting-slate-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Folder Creator</h1>
            <p className="text-charcoal-600 mt-1">Create standardized folder structures for Prospects and Clients</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-charcoal-900 mb-3">Overview</h2>
            <p className="text-charcoal-700 leading-relaxed">
              The Folder Creator is a Python utility that automates the creation of standardized folder structures 
              for Prospects and Clients in Google Drive. It helps maintain consistent organization across your sales 
              and client management workflows.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-charcoal-900 mb-3 flex items-center">
              <Terminal className="h-5 w-5 mr-2 text-consulting-slate-blue-600" />
              Usage
            </h2>
            <div className="bg-[var(--surface)] p-4 border border-[var(--rule)]">
              <ol className="list-decimal list-inside space-y-2 text-charcoal-700">
                <li>Navigate to the utilities folder:
                  <code className="block mt-1 p-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)] font-mono text-sm">
                    cd utilities/folder-creator
                  </code>
                </li>
                <li>Run the script:
                  <code className="block mt-1 p-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)] font-mono text-sm">
                    python create_folders.py
                  </code>
                </li>
                <li>Follow the prompts to select Prospect or Client and enter the name</li>
                <li>The script will create the folder structure in your configured Google Drive location</li>
              </ol>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-charcoal-900 mb-3 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-consulting-slate-blue-600" />
              Folder Structures
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[var(--slate-dim)] p-4 border border-[var(--slate)]">
                <h3 className="font-semibold text-[var(--text-sec)] mb-2">Prospect Folders</h3>
                <ul className="text-sm text-[var(--text-sec)] space-y-1">
                  <li>• Discovery & Qualification</li>
                  <li>• Correspondence</li>
                  <li>• Proposals & Offers</li>
                  <li>• Presentation Materials</li>
                  <li>• Objections & Responses</li>
                  <li>• Decision & Next Steps</li>
                  <li>• Archived (Lost - Unresponsive)</li>
                </ul>
              </div>

              <div className="bg-[var(--green-dim)] p-4 border border-[var(--green)]">
                <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-2">Client Folders</h3>
                <ul className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] space-y-1">
                  <li>• Onboarding</li>
                  <li>• Engagement Documents</li>
                  <li>• Correspondence</li>
                  <li>• Feedback & Iterations</li>
                  <li>• Invoices & Payments</li>
                  <li>• Case Studies & Testimonials</li>
                  <li>• Renewals & Upsells</li>
                  <li>• Offboarding - Completed Engagements</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Configuration</h3>
            <p className="text-sm text-[var(--mustard)]">
              Edit <code className="bg-[var(--bg-raised)] px-1 rounded">create_folders.py</code> to change the 
              <code className="bg-[var(--bg-raised)] px-1 rounded">BASE_DIR</code> path to your desired Google Drive location.
            </p>
          </div>

          <div className="bg-consulting-slate-blue-50 p-4 border border-consulting-slate-blue-200">
            <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Location</h3>
            <p className="text-sm text-consulting-slate-blue-800">
              <code className="bg-[var(--bg-raised)] px-1 rounded">utilities/folder-creator/create_folders.py</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};





